import { useAuth } from '@polvo-labs/react-auth'
import Axios from 'axios'
import { formatRelative } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import React, { useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { theme, ThemeProvider } from '../../config/theme'
import { RoomsContext } from '../../context/rooms-context'
import Room from '../../models/Room'
import { isInRole } from '../../models/User'
import { MessageReceived, StatusChange, useSocket } from '../../Socket'
import isAdmin from '../../utils/is-admin'
import Text from '../Text'
import Messages from './Messages'
import { Baloon, MessageContainer } from './Messages/styles'
import NewMessage from './NewMessage'
import { Container } from './styles'
import { mapToComponent, Message, MessageCreate, welcomeMessage } from './type'

interface Props {
  room: Room
  setRoom?: React.Dispatch<React.SetStateAction<Room | undefined>>
  visualizing: boolean
  setTitle?: (title: string) => void
  messageClick?: () => void
  onMessage?: (message?: Message) => void
  open?: boolean
}

const Chat: React.FC<Props> = ({
  room,
  visualizing = false,
  setRoom,
  setTitle,
  messageClick,
  onMessage,
  open = false,
}) => {
  const socket = useSocket()
  const { token, user } = useAuth()
  const [messages, setMessages] = useState<Message[] | undefined>(undefined)
  const [online, setOnline] = useState(room.receiver_status)
  const [page, setPage] = useState(0)
  const [submittingMessage, setSubmittingMessage] = useState(false)
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [loadingMoreMessages, setloadingMoreMossages] = useState(false)
  const [isArchivedMessages, setIsArchivedMessages] = useState(false)
  const { roomsState, roomsDispatch } = useContext(RoomsContext)
  const [roomsSocket, setRoomsSocket] = useState<Room[]>([])
  const [firstMessage, setFirstMessage] = useState<Message>(welcomeMessage)

  useEffect(() => {
    roomsState.roomsRun.concat(roomsState.roomsNotAttended).map((item) => {
      socket?.removeListener(item.uuid)
      return item
    })
    const index = roomsSocket.findIndex(item => item.uuid === room.uuid)
    if (index) {
      roomsSocket.splice(index, 1)
    }
    socket?.removeListener('message')
  }, [room.uuid])

  useEffect(() => {
    let mounted = true
    Axios({
      method: 'GET',
      url: `${process.env.REACT_APP_API_URL}/system_settings/room-init-message`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then(({ data }) => {
      if (mounted && data.data.content) {
        const newFirstMessage = { ...firstMessage, content: data.data.content }
        setFirstMessage(newFirstMessage)
      }
    }).catch(() => {
    })
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    setMessages(undefined)
  }, [room.uuid])


  useEffect(() => {
    if (messages?.length === 0 && !isInRole(user, 'admin')) {
      setMessages([firstMessage])
    }
  }, [messages, firstMessage])

  useEffect(() => {
    let mounted = true
    if (socket && !roomsSocket.find(item => item.uuid === room.uuid)) {
      setRoomsSocket([...roomsSocket, room])
      socket.emit('join', {
        room: room.uuid,
      })

      socket.emit('join', {
        room: 'attendant',
      })

      socket.emit('join', {
        room: 'notification',
      })

      socket.on('status', (data: StatusChange) => {

        if (Number(data.user) !== Number(user.id)) {
          setOnline(data.status)
        }
      })
      socket.on('message', (data: MessageReceived) => {
        if (data.room === room.uuid) {
          if (onMessage)
            onMessage(data.payload)
          setMessages(prev => {
            if (prev) {
              return [
                data.payload,
                ...prev,
              ]
            }
            return [data.payload]
          })
          roomsDispatch({ type: 'set_visualizing' })
        }
        if (data.room !== room.uuid || (!isAdmin(user) && data && data.payload.user?.id !== room.user.id)) {
          const m = data.payload
          const Component = mapToComponent[m.type]
          const type = 'received'
          if (roomsState.room) {
            room.unread_messages_count++
            setRoom!({ ...room })
            roomsDispatch({ type: 'set_room', payload: { ...roomsState, room: room } })
          }
          const Msg = () => (
            <ThemeProvider
              theme={theme}
            >
              <Container
                onClick={() => {
                  if (setRoom) {
                    roomsState.roomsRun.map((item) => {
                      if (data.room === item.uuid) {
                        setRoom(item)
                      }
                      return item
                    })
                  }
                  if (messageClick) {
                    messageClick()
                  }
                }}
              >
                <MessageContainer
                  style={{ maxWidth: '100%' }}
                  key={m.id}
                  type={type}
                  data-id={m.id}
                >
                  <Text
                    tag="span"
                    size="hint"
                  >
                    {m.user?.name}
                  </Text>
                  <Baloon
                    read={true}
                    isFirstSentFromUser={true}
                    isLastSentFromUser={true}
                    type={type}
                    full={m.type === 'AUDIO'}
                  >
                    <Component
                      message={m}
                      type={type}
                    />
                  </Baloon>
                  <Text
                    tag="span"
                    size="hint"
                  >
                    {formatRelative(new Date(m.created_at!), new Date(), {
                      locale: ptBR,
                    })}
                  </Text>
                </MessageContainer>
              </Container>
            </ThemeProvider>
          )
          if (!isAdmin(user) && !open)
            toast(<Msg />)
          else if (isAdmin(user))
            toast(<Msg />)
        }
        roomsDispatch({ type: 'update_room_last_message', uui: data.room, message: data.payload })
      })

      socket.on('read_receipts', (data: any) => {
        if (data.room === room.uuid) {
          const messages = data.messages
          if (room.unread_messages_count - data.messages.length >= 0 && data.user === user.id) {
            room.unread_messages_count = room.unread_messages_count - data.messages.length
            setRoom!({ ...room })
            if (roomsState.room) {
              roomsDispatch({ type: 'set_room', payload: { ...roomsState, room: room } })
            }
          }
          if (mounted && messages && messages.length > 0) {
            setMessages(prev => {
              if (prev) {
                const newMessages = [...prev]
                newMessages.forEach(m => {
                  if (messages.includes(m.id)) {
                    m.is_read = true
                  }
                })
                return newMessages
              }
              return prev
            })
          }
        }
      })

      socket.on('attendant', (data: any) => {

        const name = data.attendant?.name || '- IPTU admin'
        if (data.user?.id === user.id) {
          if (setRoom !== undefined) {
            const newRoom = { ...room, ...{ attendant: data.attendant, attendant_id: data.attendant_id } }
            setRoom(newRoom)
          }
          if (room.attendant_id) {
            toast.info(`Está sendo atendido por ${name}`)
          }
        }
      })

      socket.on('notification', (data: any) => {

        toast.info(`JSON notification ${JSON.stringify(data)}`)
      })

    }
    return () => {
      mounted = false
    }
  }, [socket, room, user.id, roomsDispatch, roomsSocket, setRoomsSocket])

  useEffect(() => {
    let mounted = true
    if (room && !messages) {
      Axios({
        method: 'GET',
        url: `${process.env.REACT_APP_API_URL}/chat/rooms/${room.uuid}/messages/${isArchivedMessages}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then(({ data }) => {
        if (mounted && roomsState.room && room.uuid === roomsState.room?.uuid) {
          setMessages(data.data)
          setHasMoreMessages(data.meta.current_page !== data.meta.last_page)
          setPage(data.meta.current_page)
          roomsDispatch({ type: 'set_visualizing' })
        }
      })
    }
    return () => {
      mounted = false
    }
  }, [room.uuid, messages, user, token, roomsDispatch])

  useEffect(() => {
    let mounted = true

    if (room) {
      setloadingMoreMossages(true)
      Axios({
        method: 'GET',
        url: `${process.env.REACT_APP_API_URL}/chat/rooms/${room.uuid}/messages/${isArchivedMessages}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then(({ data }) => {
        if (mounted) {
          setloadingMoreMossages(false)
          setMessages(data.data)
          setHasMoreMessages(data.meta.current_page !== data.meta.last_page)
          setPage(data.meta.current_page)
          roomsDispatch({ type: 'set_visualizing' })
        }
      })
    }
    return () => {
      mounted = false
    }
  }, [isArchivedMessages])

  useEffect(() => {
    let mounted = true
    if (messages) {
      if (roomsState.visualizing) {
        const unread = messages.filter(m => {
          return !m.is_read && m.user?.id !== user.id
        })

        if (unread && unread.length > 0) {
          const unreadIds = unread.map(m => m.id)

          setMessages(prev => {
            if (prev) {
              const messages = prev.map(m => {
                if (unreadIds.includes(m.id)) {
                  m.is_read = true
                }
                return m
              })
              return messages
            }
            return prev
          })

          Axios({
            method: 'PUT',
            url: `${process.env.REACT_APP_API_URL}/chat/rooms/${room.uuid}/messages/read`,
            headers: {
              Authorization: `Bearer ${token}`,
            },
            data: {
              messages: unreadIds.map(id => ({ id })),
            },
          }).then(({ data }) => {

          }).catch(() => {
            if (mounted) {
              toast.error('Ocorreu ao marcar mensagens como lidas')
            }
          })
        }
      }
    }
    return () => {
      mounted = false
    }
  }, [messages, room.uuid, token, user, roomsState.visualizing])

  const loadMoreMessages = async () => {
    setloadingMoreMossages(true)
    try {
      const { data } = await Axios({
        method: 'GET',
        url: `${process.env.REACT_APP_API_URL}/chat/rooms/${room.uuid}/messages/${isArchivedMessages}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          page: page + 1,
        },
      })
      setMessages(prev => {
        if (prev) {
          return [
            ...prev,
            ...data.data,
          ]
        }
      })
      setHasMoreMessages(data.meta.current_page !== data.meta.last_page)
      setPage(data.meta.current_page)
      setloadingMoreMossages(false)
    } catch (e) {
      toast.error('Ocorreu um erro ao carregar mais mensagens')
      setloadingMoreMossages(false)
    }
  }

  const submitMessage = (message: MessageCreate) => {
    setSubmittingMessage(true)

    return Axios({
      method: 'POST',
      url: `${process.env.REACT_APP_API_URL}/chat/rooms/${room.uuid}/messages`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: message,
    }).then(({ data }) => {
      roomsDispatch({ type: 'update_room_last_message', uui: room.uuid, message: data.data })
      setSubmittingMessage(false)
    }).catch(() => {
      toast.error('Ocorreu um erro ao enviar mensagem')
      setSubmittingMessage(false)
    })
  }

  return (
    <Container>
      <Messages
        room={roomsState.room}
        messages={messages || []}
        loadingMessages={messages === undefined}
        hasMoreMessages={hasMoreMessages}
        loadMoreMessages={loadMoreMessages}
        loadingMoreMessages={loadingMoreMessages}
        visualizing={visualizing && messages !== undefined}
        setIsArchivedMessages={setIsArchivedMessages}
        isArchivedMessages={isArchivedMessages}
      />

      <NewMessage
        online={online}
        submitMessage={submitMessage}
        submittingMessage={submittingMessage}
      />
    </Container>
  )
}

export default Chat
